import { createBaseApi } from '../baseApi';

// Create the specific API instance for order items
export const enhancedOrderItemApi = createBaseApi('orderItemApi', ['OrderItem', 'Request', 'FulfillmentGroup', 'Order']);

export const orderItemApi = enhancedOrderItemApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get order item details
    getOrderItemDetails: builder.query({
      query: (orderItemId) => `/order-items/${orderItemId}`,
      providesTags: (result, error, orderItemId) => [
        { type: 'OrderItem', id: orderItemId },
      ],
    }),

    // Get order items by fulfillment group
    getOrderItemsByFulfillmentGroup: builder.query({
      query: (fulfillmentGroupId) => `/fulfillment-groups/${fulfillmentGroupId}/order-items`,
      providesTags: (result = [], error, fulfillmentGroupId) => [
        ...result.map(({ id }) => ({ type: 'OrderItem', id })),
        { type: 'OrderItem', id: `FG_${fulfillmentGroupId}` },
        { type: 'OrderItem', id: 'LIST' },
      ],
    }),

    // Cancel order item
    cancelOrderItem: builder.mutation({
      query: ({ orderItemId, cancelReason, cancelComments }) => ({
        url: `/order-items/${orderItemId}/cancel`,
        method: 'PUT',
        body: { cancelReason, cancelComments }
      }),
      async onQueryStarted({ orderItemId }, { dispatch, queryFulfilled }) {
        // Optimistic update for the specific order item
        const patchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.status = 'canceled';
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic update if mutation fails
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Confirm order item delivery
    confirmOrderItem: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/confirm`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Initiate item return
    initiateItemReturn: builder.mutation({
      query: ({ orderItemId, reason, quantity, comments, fulfillmentGroupId }) => ({
        url: `/order-items/${orderItemId}/initiate-return`,
        method: 'POST',
        body: { reason, quantity, comments, fulfillmentGroupId }
      }),
      async onQueryStarted({ orderItemId, reason, quantity, comments, fulfillmentGroupId }, { dispatch, queryFulfilled }) {
        // Optimistic update for the specific order item
        const patchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.status = 'return_requested_by_customer';
              draft.returnRequest = {
                reason,
                quantity: quantity || draft.quantity,
                comments: comments || '',
                requestDate: new Date().toISOString(),
              };
            }
          })
        );
        
        // Additionally update FG cache optimistically so buttons refresh immediately
        if (fulfillmentGroupId) {
          const { fulfillmentGroupApi } = await import('./fulfillmentGroupApi');
          dispatch(
            fulfillmentGroupApi.util.updateQueryData('getFulfillmentGroupDetails', fulfillmentGroupId, draftFg => {
              if (draftFg) {
                // Set status to partial_return_requested if not already in a return status
                if (!draftFg.status?.toLowerCase().includes('return')) {
                  draftFg.status = 'partial_return_requested';
                }
                // Update matching order item inside FG
                const targetItem = draftFg.orderItems?.find(it => it.id === orderItemId);
                if (targetItem) {
                  targetItem.status = 'return_requested_by_customer';
                  targetItem.returnRequest = {
                    reason,
                    quantity: quantity || targetItem.quantity,
                    comments: comments || '',
                    requestDate: new Date().toISOString()
                  };
                }
              }
            })
          );
        }
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId, fulfillmentGroupId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'FulfillmentGroup', id: fulfillmentGroupId },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Cancel return request
    cancelReturnRequest: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/cancel-return`,
        method: 'PUT',
      }),
      async onQueryStarted({ orderItemId }, { dispatch, queryFulfilled, getState }) {
        
        // Get the current order item data to access fulfillment group info
        const currentItemData = orderItemApi.endpoints.getOrderItemDetails.select(orderItemId)(getState());
        const fulfillmentGroupId = currentItemData?.data?.fulfillmentGroup?.id;
        

        // Optimistic update for the specific order item
        const itemPatchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.status = 'delivered'; // Restore to delivered status
              draft.returnRequest = null;
            }
          })
        );

        // If we have fulfillment group info, optimistically update it too
        let fgPatchResult = null;
        if (fulfillmentGroupId) {
          // Note: We'll rely on cache invalidation to update the FG cache
          // The manual update will be done in the modal component after the mutation
        }
        
        try {
          const result = await queryFulfilled;
        } catch (error) {
          console.error('❌ cancelReturnRequest mutation failed, reverting optimistic updates:', error);
          // Revert optimistic updates if mutation fails
          itemPatchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId }) => {
        // Get fulfillment group ID from the result data
        const fulfillmentGroupId = result?.data?.fulfillmentGroupId;
        
        const tags = [
          { type: 'OrderItem', id: orderItemId },
          { type: 'OrderItem', id: 'LIST' },
          { type: 'Request', id: 'LIST' },
          { type: 'FulfillmentGroup', id: 'LIST' },
          { type: 'Order', id: 'LIST' },
        ];
        
        // Add specific fulfillment group tag if we have the ID
        if (fulfillmentGroupId) {
          tags.push({ type: 'FulfillmentGroup', id: fulfillmentGroupId });
        }
        
        return tags;
      },
    }),

    // Start return shipping
    startReturnShipping: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/start-return`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Request', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Update order item status by request (merchant action)
    updateOrderItemStatus: builder.mutation({
      query: ({ orderItemId, status }) => ({
        url: `/order-items/${orderItemId}/status`,
        method: 'PUT',
        body: { status }
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Get item tracking
    getItemTracking: builder.query({
      query: (orderItemId) => `/order-items/${orderItemId}/tracking`,
      providesTags: (result, error, orderItemId) => [
        { type: 'OrderItem', id: orderItemId }
      ],
    }),

    // Get return tracking
    getReturnTracking: builder.query({
      query: (orderItemId) => `/order-items/${orderItemId}/return-tracking`,
      providesTags: (result, error, orderItemId) => [
        { type: 'OrderItem', id: orderItemId }
      ],
    }),

    // Confirm item delivery
    confirmItemDelivery: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/confirm-delivery`,
        method: 'PUT',
      }),
      async onQueryStarted({ orderItemId }, { dispatch, queryFulfilled }) {
        // Optimistic update
        const patchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.status = 'delivered';
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    // Keep rental item (purchase)
    keepRentalItem: builder.mutation({
      query: ({ orderItemId, paymentDetails }) => ({
        url: `/order-items/${orderItemId}/keep-rental`,
        method: 'POST',
        body: { paymentDetails }
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    // Get keep item pricing for rental
    getKeepItemPricing: builder.query({
      query: (orderItemId) => `/order-items/${orderItemId}/keep-item-pricing`,
      providesTags: (result, error, orderItemId) => [
        { type: 'OrderItem', id: orderItemId }
      ],
    }),

    // Request to keep a rental item
    requestKeepItem: builder.mutation({
      query: ({ orderItemId }) => ({
        url: `/order-items/${orderItemId}/request-keep`,
        method: 'POST',
      }),
      async onQueryStarted({ orderItemId }, { dispatch, queryFulfilled }) {
        // Optimistic update
        const patchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.keepItemRequested = true;
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    // Process keep item payment
    processKeepItemPayment: builder.mutation({
      query: ({ orderItemId, paymentMethodId }) => ({
        url: `/payments/keep-item`,
        method: 'POST',
        body: { orderItemId, paymentMethodId }
      }),
      async onQueryStarted({ orderItemId }, { dispatch, queryFulfilled }) {
        // Optimistic update
        const patchResult = dispatch(
          orderItemApi.util.updateQueryData('getOrderItemDetails', orderItemId, (draft) => {
            if (draft) {
              draft.keepItemApproved = true;
              draft.status = 'purchased';
            }
          })
        );
        
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

    // Extend rental
    extendRental: builder.mutation({
      query: ({ orderItemId, extensionPeriod, paymentDetails }) => ({
        url: `/order-items/${orderItemId}/extend-rental`,
        method: 'POST',
        body: { extensionPeriod, paymentDetails }
      }),
      invalidatesTags: (result, error, { orderItemId }) => [
        { type: 'OrderItem', id: orderItemId },
        { type: 'OrderItem', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'FulfillmentGroup', id: 'LIST' },
      ],
    }),

  }),
  overrideExisting: false,
});

export const {
  useGetOrderItemDetailsQuery,
  useGetOrderItemsByFulfillmentGroupQuery,
  useCancelOrderItemMutation,
  useConfirmOrderItemMutation,
  useInitiateItemReturnMutation,
  useCancelReturnRequestMutation,
  useStartReturnShippingMutation,
  useUpdateOrderItemStatusMutation,
  useGetItemTrackingQuery,
  useGetReturnTrackingQuery,
  useConfirmItemDeliveryMutation,
  useKeepRentalItemMutation,
  useExtendRentalMutation,
  useGetKeepItemPricingQuery,
  useRequestKeepItemMutation,
  useProcessKeepItemPaymentMutation,
} = orderItemApi; 